import React, { useEffect, useState, useRef } from 'react';
import InfoBanner from '../components/InfoBanner';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { fetchRedis, deleteRedis, addRedis, updateRedisStatus, RedisInstance } from '../store/slices/redisSlice';
import { fetchFlavors } from '../store/slices/flavorsSlice';
import axiosInstance from '../utility/axiosInstance';
import { useToast } from '../components/ToastContext';
import { useConfirm } from '../components/ConfirmContext';
import Select, { SingleValue } from 'react-select';
import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import Modal from '../components/Modal';
import ManagedBackupsModal from '../components/ManagedBackupsModal';
import TrialLock from '../components/TrialLock';
import { FaPlus, FaSyncAlt, FaRegCopy, FaCheck } from 'react-icons/fa';
import '../styles/InstancesPage.scss';
import '../styles/DatabasesPage.scss';
import useAutoOpenCreateModal from '../hooks/useAutoOpenCreateModal';
import { isFlavorOptionDisabled, platformImage, toFlavorOptions } from '../utility/flavorOptions';
import { NameField, SecretField, formatSizeOption } from '../components/CreateFormFields';
import { secretOk } from '../utility/fieldRules';

type SelectOption = { value: string; label: string; isDisabled?: boolean };

const ITEMS_PER_PAGE = 10;

const RedisPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { showToast } = useToast();
    const { confirm } = useConfirm();
    const [backupsFor, setBackupsFor] = useState<any>(null);
    const { instances, status } = useSelector((s: RootState) => s.redis);
    const flavors = useSelector((s: RootState) => s.flavors.data);
    const images = useSelector((s: RootState) => s.images.data);
    const vmInstances = useSelector((s: RootState) => s.instances.data);

    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ name: '', password: '', version: '7' });
    const [flavorOption, setFlavorOption] = useState<SelectOption | null>(null);
    const [keypairOption, setKeypairOption] = useState<SelectOption | null>(null);
    const [keypairs, setKeypairs] = useState<SelectOption[]>([]);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [networkId, setNetworkId] = useState<string | null>(null);
    const [justCreated, setJustCreated] = useState<RedisInstance & { password: string } | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const pollRefs = useRef<Record<number, NodeJS.Timeout>>({});

    const creationLocked = useAutoOpenCreateModal(setShowCreate);

    useEffect(() => {
        dispatch(fetchRedis());
        dispatch(fetchFlavors());
    }, [dispatch]);

    useEffect(() => {
        if (!showCreate) return;
        Promise.all([
            axiosInstance.get('/v1/keypairs'),
            axiosInstance.get('/v1/private_networks'),
        ]).then(([kpRes, netRes]) => {
            const kps = (Array.isArray(kpRes.data) ? kpRes.data : [])
                .map((k: any) => ({ value: k.keypair?.name ?? '', label: k.keypair?.name ?? '' }))
                .filter((k: SelectOption) => k.value);
            setKeypairs(kps);
            if (kps.length === 1) setKeypairOption(kps[0]);
            const nets = Array.isArray(netRes.data) ? netRes.data : [];
            if (nets[0]?.id) setNetworkId(nets[0].id);
        }).catch((_e) => { /* ignore */ });
    }, [showCreate]);

    useEffect(() => {
        instances.filter(i => i.status === 'provisioning').forEach(inst => {
            if (pollRefs.current[inst.id]) return;
            pollRefs.current[inst.id] = setInterval(async () => {
                try {
                    const listRes = await axiosInstance.get('/v1/instances');
                    const all = Array.isArray(listRes.data) ? listRes.data : [];
                    const vm = all.find((i: any) => i.name === `redis-${inst.name}` && i.status === 'ACTIVE');
                    if (vm) {
                        const ip = vm.metadata?.floatingIp ?? null;
                        clearInterval(pollRefs.current[inst.id]);
                        delete pollRefs.current[inst.id];
                        await axiosInstance.patch(`/v1/redis/${inst.id}`, { status: 'ready', floating_ip: ip });
                        dispatch(updateRedisStatus({ id: inst.id, status: 'ready', floating_ip: ip }));
                    }
                } catch (_e) { /* keep polling */ }
            }, 15000);
        });
        return () => { Object.values(pollRefs.current).forEach(clearInterval); };
    }, [instances, dispatch]);

    useEffect(() => {
        setCurrentPage(0);
    }, [instances.length]);

    const totalInstances = instances.length;
    const totalPages = Math.ceil(totalInstances / ITEMS_PER_PAGE);
    const paginatedInstances = instances.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    );

    // Every number from /v1/flavors; a size the datastore image cannot boot is locked with the reason, from that image's
    // own min_ram/min_disk (/v1/images), as on the instance form. One machine's price.
    const flavorOptions: SelectOption[] = toFlavorOptions(flavors, undefined, { image: platformImage(images, 'datastore') });

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!flavorOption) { showToast('Select a server size', 'error'); return; }
        if (!keypairOption) { showToast('Select an SSH keypair', 'error'); return; }
        if (!networkId) { showToast('No private network found. Create one first.', 'error'); return; }
        if (!form.name.trim()) { showToast('Enter a name', 'error'); return; }

        setCreating(true);
        try {
            const res = await axiosInstance.post('/v1/redis', {
                name: form.name.trim(),
                password: form.password.trim() || undefined,
                version: form.version,
                flavor_id: flavorOption.value,
                network_id: networkId,
                keypair_name: keypairOption.value,
                security_group_names: ['default'],
            });
            setJustCreated(res.data);
            dispatch(addRedis(res.data));
            setShowCreate(false);
            setForm({ name: '', password: '', version: '7' });
            setFlavorOption(null);
            showToast('Redis provisioning started', 'success');
        } catch (err: any) {
            showToast(err.response?.data?.error ?? 'Failed to create Redis', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (inst: RedisInstance) => {
        const proceed = await confirm({
            title: 'Delete Redis',
            message: `Delete "${inst.name}"? This will destroy the server and all data.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;
        setDeletingId(inst.id);
        try {
            await dispatch(deleteRedis(inst.id)).unwrap();
            showToast(`${inst.name} deleted`, 'success');
        } catch (err: any) {
            showToast(err.message ?? 'Failed to delete', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const handleCopyConnection = (inst: RedisInstance) => {
        const str = connString(inst);
        if (!str) return;
        const doCopy = () => {
            if (navigator.clipboard && window.isSecureContext) {
                return navigator.clipboard.writeText(str);
            }
            const el = document.createElement('textarea');
            el.value = str;
            el.style.position = 'fixed';
            el.style.opacity = '0';
            document.body.appendChild(el);
            el.focus();
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            return Promise.resolve();
        };
        doCopy().then(() => {
            setCopiedId(inst.id);
            showToast('Connection string copied', 'success');
            setTimeout(() => setCopiedId(null), 2000);
        }).catch(() => showToast('Failed to copy', 'error'));
    };

    // Managed Redis is private (see the banner) - connect from an instance on the same private
    // network using the INTERNAL IP (the fixed address on the private network, the same value shown
    // as "Internal IP" on Instance Details). Falls back to the floating IP if the VM isn't loaded.
    const hostFor = (inst: RedisInstance): string | null => {
        if (inst.private_ip) return inst.private_ip;
        const vm: any = (vmInstances ?? []).find((i: any) => i.id === (inst as any).instance_id);
        const internalIp = vm
            ? (Object.values(vm.addresses ?? {}).flat() as any[]).find(a => a?.["OS-EXT-IPS:type"] === "fixed")?.addr
            : null;
        return internalIp ?? inst.floating_ip ?? null;
    };

    const connString = (inst: RedisInstance, password?: string) => {
        const host = hostFor(inst);
        return host ? `redis://:${password ?? '****'}@${host}:${inst.port}` : null;
    };

    const isLoading = status === 'loading';

    return (
        <div className="keypairs-page">
            <div className="page-header">
                <p className="page-subtitle">
                    Dedicated Redis servers with persistence and AOF logging. Ready in minutes.
                </p>
                <a href="https://docs.redu.cloud/Guides/redis" target="_blank" rel="noopener noreferrer"
                   className="docs-link">
                    Documentation →
                </a>
            </div>

            <InfoBanner id="redis-private-access">
                <span>Managed Redis is <strong>private</strong> - reachable only from an instance on the <strong>same private network</strong>, using its private IP (e.g. <code>redis://:****@10.1.x.x:6379</code>). It is not exposed to the public internet.</span>
            </InfoBanner>

            <TrialLock>
                <button className="create-button" onClick={() => setShowCreate(true)} title="New Redis">
                    <FaPlus />
                    <span>New Redis</span>
                </button>
            </TrialLock>

            {/* Just-created credentials banner - shown once */}
            {justCreated && (
                <div className="db-credentials-banner">
                    <div className="db-cred-header">
                        <span className="db-cred-title">✓ <strong>{justCreated.name}</strong> is provisioning</span>
                        <button className="db-cred-dismiss" onClick={() => setJustCreated(null)}>✕</button>
                    </div>
                    <p className="db-cred-note">Save these credentials now - the password won&apos;t be shown again.</p>
                    <div className="db-cred-grid">
                        <div className="db-cred-row"><span>Host</span><code>{hostFor(justCreated) ?? 'Provisioning… check back in 5 min'}</code></div>
                        <div className="db-cred-row"><span>Port</span><code>6379</code></div>
                        <div className="db-cred-row"><span>Password</span><code>{justCreated.password}</code></div>
                        {connString(justCreated, justCreated.password) && (
                            <div className="db-cred-row">
                                <span>Connection string</span>
                                <code className="db-cred-conn"
                                    onClick={() => { navigator.clipboard?.writeText(connString(justCreated, justCreated.password)!); showToast('Copied', 'success'); }}>
                                    {connString(justCreated, justCreated.password)} <span className="copy-hint">click to copy</span>
                                </code>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Desktop table */}
            <div className="desktop-view">
                <div className="table-container">
                    <table className="keypairs-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Version</th>
                                <th>Connection</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={5} className="loading-state">
                                        <FaSyncAlt className="spin" /> Loading Redis instances...
                                    </td>
                                </tr>
                            ) : paginatedInstances.length > 0 ? (
                                paginatedInstances.map(inst => {
                                    const conn = connString(inst);
                                    return (
                                    <tr key={inst.id}>
                                        <td className="keypair-name">{inst.name}</td>
                                        <td>Redis {inst.version}</td>
                                        <td className="db-conn">
                                            {conn ? (
                                                <div className="db-conn-wrapper">
                                                    <code>{conn}</code>
                                                    <button
                                                        className={`db-copy-btn${copiedId === inst.id ? ' copied' : ''}`}
                                                        onClick={() => handleCopyConnection(inst)}
                                                        title="Copy connection string"
                                                    >
                                                        {copiedId === inst.id ? <FaCheck /> : <FaRegCopy />}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="db-provisioning">Provisioning…</span>
                                            )}
                                        </td>
                                        <td><span className={`db-status db-status-${deletingId === inst.id ? 'provisioning' : inst.status}`}>{deletingId === inst.id ? 'deleting…' : inst.status}</span></td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <Select
                                                options={[{ value: 'backups', label: 'Backups' }, { value: 'delete', label: 'Delete Redis' }]}
                                                onChange={opt => { if (opt?.value === 'delete') handleDelete(inst); else if (opt?.value === 'backups') setBackupsFor(inst); }}
                                                placeholder="Actions"
                                                isSearchable={false}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={getActionSelectStyles()}
                                            />
                                        </td>
                                    </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={5} className="empty-state">No Redis instances yet</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={currentPage === 0}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >← Prev</button>
                            <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalInstances)} of {totalInstances}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={currentPage >= totalPages - 1}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >Next →</button>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile cards */}
            <div className="mobile-view">
                {isLoading ? (
                    <div className="loading-state">
                        <FaSyncAlt className="spin" />
                        <p>Loading Redis instances...</p>
                    </div>
                ) : (
                    <div className="cards-container">
                        {paginatedInstances.length > 0 ? paginatedInstances.map(inst => {
                            const conn = connString(inst);
                            return (
                            <div key={inst.id} className="keypair-card">
                                <div className="card-header">
                                    <h3 className="card-title">{inst.name}</h3>
                                    <div style={{ width: '100%', marginTop: 12 }}>
                                        <Select
                                            options={[{ value: 'backups', label: 'Backups' }, { value: 'delete', label: 'Delete Redis' }]}
                                            onChange={opt => { if (opt?.value === 'delete') handleDelete(inst); else if (opt?.value === 'backups') setBackupsFor(inst); }}
                                            placeholder="Actions"
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            styles={getActionSelectStyles()}
                                        />
                                    </div>
                                </div>
                                <div className="card-content">
                                    <div className="fingerprint"><span className="label">Version:</span><span className="value">Redis {inst.version}</span></div>
                                    <div className="fingerprint"><span className="label">Status:</span><span className="value"><span className={`db-status db-status-${deletingId === inst.id ? 'provisioning' : inst.status}`}>{deletingId === inst.id ? 'deleting…' : inst.status}</span></span></div>
                                    <div className="fingerprint">
                                        <span className="label">Connection:</span>
                                        <span className="value db-conn">
                                            {conn ? (
                                                <span className="db-conn-wrapper">
                                                    <span>{conn}</span>
                                                    <button
                                                        className={`db-copy-btn${copiedId === inst.id ? ' copied' : ''}`}
                                                        onClick={() => handleCopyConnection(inst)}
                                                        title="Copy connection string"
                                                    >
                                                        {copiedId === inst.id ? <FaCheck /> : <FaRegCopy />}
                                                    </button>
                                                </span>
                                            ) : 'Provisioning…'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            );
                        }) : (
                            <div className="empty-state"><p>No Redis instances yet</p></div>
                        )}
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px' }}>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                    disabled={currentPage === 0}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >← Prev</button>
                                <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                    {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalInstances)} of {totalInstances}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={currentPage >= totalPages - 1}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >Next →</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Create modal */}
            <ManagedBackupsModal
                isOpen={!!backupsFor}
                onClose={() => setBackupsFor(null)}
                apiBase="/v1/redis"
                serviceId={backupsFor?.id ?? null}
                serviceName={backupsFor?.name ?? ''}
            />

            <Modal isOpen={showCreate && !creationLocked} onClose={() => setShowCreate(false)} title="New Redis Instance">
                <form onSubmit={handleCreate} className="create-form">
                    <NameField value={form.name} onChange={v => setForm(p => ({ ...p, name: v }))} placeholder="my-cache" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
                        <SecretField label="Password" value={form.password} onChange={v => setForm(p => ({ ...p, password: v }))} />
                        <div className="form-group">
                            <label>Version</label>
                            <Select
                                options={[{ value: '7', label: 'Redis 7 (recommended)' }, { value: '6', label: 'Redis 6' }]}
                                value={{ value: form.version, label: `Redis ${form.version}${form.version === '7' ? ' (recommended)' : ''}` }}
                                onChange={(v: SingleValue<SelectOption>) => setForm(p => ({ ...p, version: v?.value ?? '7' }))}
                                styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed"
                            />
                        </div>
                    </div>
                    <div className="form-group">
                        <label>Server size <span style={{ color: 'var(--color-error,#e53e3e)' }}>*</span></label>
                        <Select
                            options={flavorOptions} value={flavorOption}
                            isOptionDisabled={isFlavorOptionDisabled as any}
                            onChange={(v: SingleValue<SelectOption>) => setFlavorOption(v)}
                            formatOptionLabel={formatSizeOption}
                            placeholder="Select server size…" styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed"
                        />
                    </div>
                    <div className="form-group">
                        <label>SSH keypair <span style={{ color: 'var(--color-error,#e53e3e)' }}>*</span></label>
                        <Select
                            options={keypairs} value={keypairOption}
                            onChange={(v: SingleValue<SelectOption>) => setKeypairOption(v)}
                            placeholder="Select keypair…" styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed"
                        />
                    </div>
                    <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setShowCreate(false)}>Cancel</button>
                        <button type="submit" className="submit-button" disabled={creating || !flavorOption || !keypairOption || !form.name || !secretOk(form.password)}>
                            {creating ? 'Creating…' : 'Create Redis'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default RedisPage;
